import React, { useState } from 'react';
import classNames from 'classnames';

import { trackEvent } from '../../../analytics/trackEvent';

import { nativeSharePayload } from './shareTargets';
import { IconShare } from './icons';
import ShareModal from './ShareModal';
import css from './ShareListing.module.css';

/**
 * Phones and tablets get the OS share sheet; everything else gets our own
 * dialog. Desktop Safari and Chrome do implement navigator.share, but the
 * macOS sheet is inconsistent and skips the branded preview — so the split
 * is on pointer type, not on whether the API exists.
 */
export const shouldUseNativeShare = () =>
  typeof navigator !== 'undefined' &&
  typeof navigator.share === 'function' &&
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

/**
 * Share something: one tap to the OS share sheet on phones, our own dialog
 * on desktop. Knows nothing about what it is sharing — a listing beside the
 * heart on the product page, a profile beside the rating on Min profil —
 * only where the link goes and what the dialog should show. See
 * shareProps.js for how each kind of thing becomes these props.
 *
 * @param {Object} props
 * @param {Object} props.share - { url, title, text, contentType, itemId }
 * @param {Object} props.preview - { image, imageAlt, variants, title, subtitle, detail }
 * @param {string} props.label - the button's accessible name, and its text when labelled
 * @param {string} props.modalTitle - the dialog heading
 * @param {'disc'|'labelled'} [props.variant] - `disc` is the bare round button
 *   that floats over a listing photo; `labelled` shows the text with the icon
 *   after it, for a place with no photo to float over, like a profile
 * @param {string} [props.facebookAppId] - enables the Messenger button in the dialog
 * @param {Function} props.onManageDisableScrolling
 * @param {string} [props.className]
 */
const ShareButton = props => {
  const {
    share,
    preview,
    label,
    modalTitle,
    variant = 'disc',
    facebookAppId,
    onManageDisableScrolling,
    className,
  } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const isLabelled = variant === 'labelled';

  if (!share?.url || !share?.itemId) {
    return null;
  }
  const { url, title, text, contentType, itemId } = share;

  const handleClick = async () => {
    if (shouldUseNativeShare()) {
      trackEvent('share', { method: 'native', content_type: contentType, item_id: itemId });
      try {
        await navigator.share(nativeSharePayload({ url, title, text }));
      } catch (e) {
        // The sheet was closed without sharing — not an error.
      }
      return;
    }
    trackEvent('share_open', { method: 'modal', content_type: contentType, item_id: itemId });
    setModalOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className={classNames(isLabelled ? css.labelled : css.root, className)}
        onClick={handleClick}
        aria-label={label}
        title={label}
      >
        {isLabelled ? <span className={css.labelledText}>{label}</span> : null}
        <IconShare className={isLabelled ? css.labelledIcon : css.icon} />
      </button>
      <ShareModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        share={share}
        preview={preview}
        modalTitle={modalTitle}
        facebookAppId={facebookAppId}
      />
    </>
  );
};

export default ShareButton;
